import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  Row,
  Col,
  Space,
  Checkbox,
  Typography,
  Form,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createBranch,
  deleteBranch,
  fetchBranches,
  updateBranch,
} from "../../Redux/Slice/Admin/Branch";
import { fetchSpecialities } from "../../Redux/Slice/Admin/Speciality";
import { toast } from "react-toastify";

const Branches = ({ orgDetails }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [form] = Form.useForm();

  const { data: specialityData } = useSelector(
    (state) => state.speciality.fetch
  );

  useEffect(() => {
    dispatch(fetchSpecialities());
  }, [dispatch]);

  const { data: branchData, loading } = useSelector(
    (state) => state.branch.fetch
  );

  const {
    data: createData,
    error: createError,
    loading: createLoading,
  } = useSelector((state) => state.branch.create);

  const {
    data: updateData,
    error: updateError,
    loading: updateLoading,
  } = useSelector((state) => state.branch.update);

  const {
    data: deleteData,
    error: deleteError,
    loading: deleteLoading,
  } = useSelector((state) => state.branch.delete);

  useEffect(() => {
    dispatch(fetchBranches({ branchName: "" }));
  }, [dispatch]);

  useEffect(() => {
    if (branchData) {
      const mappedData = branchData?.data?.map((item) => ({
        ...item,
        key: item.id,
      }));
      setData(mappedData);
    }
  }, [branchData]);

  const handleSearch = (value) => {
    setSearchValue(value);
    dispatch(fetchBranches({ branchName: value })); // 🔹 Pass search payload
  };

  useEffect(() => {
    if (createData?.status) {
      toast.success(createData.message || "Branch created successfully!");
      dispatch(fetchBranches());
      setIsModalOpen(false);
      form.resetFields();
    }
    if (createError)
      toast.error(createError.message || "Branch creation failed!");
  }, [createData, createError, dispatch, form]);

  useEffect(() => {
    if (updateData?.status) {
      toast.success(updateData.message || "Branch updated successfully!");
      dispatch(fetchBranches());
      setIsModalOpen(false);
      setEditingBranch(null);
      form.resetFields();
    }
    if (updateError)
      toast.error(updateError.message || "Branch update failed!");
  }, [updateData, updateError, dispatch, form]);

  useEffect(() => {
    if (deleteData?.status) {
      toast.success(deleteData.message || "Branch deleted successfully!");
      dispatch(fetchBranches());
      setDeleteConfirm(false);
      setDeleteKey(null);
    }
    if (deleteError)
      toast.error(deleteError.message || "Branch deletion failed!");
  }, [deleteData, deleteError, dispatch]);

  // ---- Branch CRUD ----
  const openModal = (record = null) => {
    setEditingBranch(record);
    setIsModalOpen(true);

    if (record) {
      form.setFieldsValue({
        orgId: record.orgId || 1,
        branchName: record.branchName || "",
        trnNo: record.trnNo || "",
        location: record.location || "",
        branchManager: record.branchManager || "",
        mobileNo: record.mobileNo || "",
        emailId: record.emailId || "",
        address: record.address || "",
        dhaLicensoNo: record.dhaLicensoNo || "",
        dhaUsername: record.dhaUsername || "",
        password: record.password || "",
        specialityId: record.specialities?.map((s) => s.specialityId) || [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ orgId: 1, specialityId: [] });
    }
  };

  const orgId = orgDetails[0]?.id || null;

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        if (editingBranch) {
          dispatch(
            updateBranch({ ...values, branchId: editingBranch.branchId, orgId })
          );
        } else {
          dispatch(createBranch({ ...values, orgId }));
        }
      })
      .catch(() => {
        toast.error("Please fill all required fields!");
      });
  };

  const handleDelete = () => {
    if (deleteKey !== null) dispatch(deleteBranch(deleteKey));
  };

  const columns = [
    { title: "Branch Name", dataIndex: "branchName", key: "branchName" },
    { title: "Location", dataIndex: "location", key: "location" },
    { title: "Manager", dataIndex: "branchManager", key: "branchManager" },
    { title: "Contact", dataIndex: "mobileNo", key: "mobileNo" },
    { title: "Email", dataIndex: "emailId", key: "emailId" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
            shape="circle"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setDeleteKey(record.branchId);
              setDeleteConfirm(true);
            }}
            shape="circle"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input.Search
          placeholder="Search branch name"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear // 🔹 Shows close (X) icon
          style={{ width: 300 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Create
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        loading={loading}
      />

      {/* Branch Create/Edit Modal */}
      <Modal
        title={editingBranch ? "Edit Branch" : "Create New Branch"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        okText={editingBranch ? "Update" : "Create"}
        maskClosable={false}
        bodyStyle={{ maxHeight: "75vh", overflowY: "auto", padding: 16 }}
        style={{ top: 20 }}
        width={900}
        confirmLoading={editingBranch ? updateLoading : createLoading}
      >
        <Form form={form} layout="vertical">
          <h3>Basic Information</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Branch Name"
                name="branchName"
                rules={[{ required: true, message: "Branch name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="TRN Number"
                name="trnNo"
                rules={[{ required: true, message: "TRN Number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Location"
                name="location"
                rules={[{ required: true, message: "Location is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Branch Manager"
                name="branchManager"
                rules={[
                  { required: true, message: "Branch Manager is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Mobile No"
                name="mobileNo"
                rules={[
                  { required: true, message: "Mobile number is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="emailId"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Invalid email format" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: "Address is required" }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <h3>DHA Credentials</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="DHA License No"
                name="dhaLicensoNo"
                rules={[
                  { required: true, message: "DHA License No is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="DHA Username"
                name="dhaUsername"
                rules={[
                  { required: true, message: "DHA Username is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="DHA Password"
                name="password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
          </Row>

          <h3>Specialities</h3>
          <Form.Item
            name="specialityId"
            rules={[
              { required: true, message: "Select at least one speciality" },
            ]}
          >
            <Checkbox.Group style={{ width: "100%" }}>
              <Row>
                {specialityData?.data?.map((spec) => (
                  <Col span={8} key={spec.specialityId}>
                    <Checkbox value={spec.specialityId}>
                      {spec.specialityName}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={deleteConfirm}
        title="Confirm Delete"
        onCancel={() => setDeleteConfirm(false)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteLoading }}
      >
        <Typography>Are you sure you want to delete this Branch?</Typography>
      </Modal>
    </>
  );
};

export default Branches;
